import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Theme = 'boho-earth' | 'golden-dusk' | 'rose-quartz' | 'altrosa' | 'prestige-navy' | 'sage-forest' | 'indigo-bloom' | 'midnight-gold';

export const THEMES: { id: Theme; name: string; primary: string; sidebar: string }[] = [
  { id: 'boho-earth',    name: 'Boho Earth',    primary: '#8B6F47', sidebar: '#5C4330' },
  { id: 'golden-dusk',   name: 'Golden Dusk',   primary: '#C8860A', sidebar: '#7A4504' },
  { id: 'rose-quartz',   name: 'Rose Quartz',   primary: '#C2607A', sidebar: '#7B2040' },
  { id: 'altrosa',       name: 'Altrosa',       primary: '#C08888', sidebar: '#2E3234' },
  { id: 'prestige-navy', name: 'Prestige Navy', primary: '#C9A84C', sidebar: '#0D1830' },
  { id: 'sage-forest',   name: 'Sage Forest',   primary: '#2A7A62', sidebar: '#0F4035' },
  { id: 'indigo-bloom',  name: 'Indigo Bloom',  primary: '#5B4BE8', sidebar: '#281E94' },
  { id: 'midnight-gold', name: 'Midnight Gold', primary: '#C49A2A', sidebar: '#0F172A' },
];

const THEME_MIGRATION: Record<string, Theme> = {
  'desert-rose': 'boho-earth',
  'klares-blau': 'indigo-bloom',
  'fresh-teal': 'sage-forest',
  'soft-violet': 'indigo-bloom',
};

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function migrateTheme(stored: string | null): Theme {
  if (!stored) return 'midnight-gold';
  if (THEMES.some(t => t.id === stored)) return stored as Theme;
  const migrated = THEME_MIGRATION[stored];
  if (migrated) {
    localStorage.setItem('cd365_theme', migrated);
    return migrated;
  }
  return 'midnight-gold';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => migrateTheme(localStorage.getItem('cd365_theme') || localStorage.getItem('theme'))
  );

  const applyTheme = useCallback((t: Theme) => {
    document.documentElement.setAttribute('data-theme', t);
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem('cd365_theme', t);
    document.documentElement.setAttribute('data-theme', t);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

/** Non-throwing variant — returns null outside ThemeProvider */
export function useThemeOptional(): ThemeContextType | null {
  return useContext(ThemeContext) ?? null;
}
